import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';

// Data
import {
  ASSESSMENT_SESSION_FIELDS,
  ICE_SHEET_CONFLICT_FIELDS,
  SESSION_SCHEDULE_REFETCH_QUERIES,
} from '../../../lib/queries/assessment-session.queries';
import { IEditAssessmentSessionFields } from './edit-assessment-session.interface'

// Components
import { EditAssessmentSessionContainer } from './edit-assessment-session.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildCreateAgeGroup } from '../../../lib/services/age-group.service';
import { useRetryKey } from '../../../lib/hooks/use-retry-key';

const GET_EDIT_ASSESSMENT_SESSION = gql`
  query GetEditAssessmentSession($assessmentSessionId: ID!, $assessmentId: ID!) {
    assessmentSession(_id: $assessmentSessionId) {
      ...AssessmentSessionFields
      isPlayerNotificationEnabled
      iceConflicts {
        ...IceSheetConflictFields
      }
      ageGroup {
        _id
        name
      }
    }
    ageGroups(assessmentId: $assessmentId) {
      _id
      name
    }
  }

  ${ASSESSMENT_SESSION_FIELDS}
  ${ICE_SHEET_CONFLICT_FIELDS}
`;

const EDIT_SESSION = gql`
  mutation EditAssessmentSession($_id: ID!, $data: UpdateAssessmentSessionInput!, $notify: Boolean, $operationId: String) {
    updateAssessmentSession(_id: $_id, data: $data, notify: $notify, operationId: $operationId) {
      ...AssessmentSessionFields
    }
  }

  ${ASSESSMENT_SESSION_FIELDS}
`;

interface IEditAssessmentSessionGraphQL {
  assessmentSessionId: string;
}

export const EditAssessmentSessionGraphQL: React.FC<IEditAssessmentSessionGraphQL> = ({ assessmentSessionId }) => {
  const user = getAuthUser();
  const { data, loading, error } = useQuery(GET_EDIT_ASSESSMENT_SESSION, {
    skip: !user,
    variables: { assessmentSessionId, assessmentId: user?.settings.activeAssessmentId },
    fetchPolicy: 'cache-and-network'
  });

  const ageGroups = (data && data.ageGroups) || [];
  const assessmentSession = data && data.assessmentSession;

  const { createAgeGroup } = buildCreateAgeGroup();
  const [updateAssessmentSessionMutation]  = useMutation(EDIT_SESSION);
  const withRetryKey = useRetryKey();

  // A retry of the same edit reuses its operationId, so the server never announces it twice.
  const updateAssessmentSession = (data: IEditAssessmentSessionFields, notify?: boolean) => withRetryKey({ data, notify }, (operationId) => updateAssessmentSessionMutation({
    variables: { _id: assessmentSessionId, data, notify, operationId },
    optimisticResponse: {
      __typename: 'Mutation',
      updateAssessmentSession: {
        __typename: 'AssessmentSession',
        _id: assessmentSessionId,
        ...data
      }
    },
    // Apollo normalizes the returned session by `_id`, but every view here reads a
    // value computed across the schedule — RSVP totals, this session's ice conflicts,
    // the OTHER session's conflicts, and the grouping stage's readiness. None of those
    // can be recomputed from one row, so the whole set is refetched.
    refetchQueries: SESSION_SCHEDULE_REFETCH_QUERIES,
  }));

  return <EditAssessmentSessionContainer
    assessmentSession={assessmentSession}
    updateAssessmentSession={updateAssessmentSession}
    createAgeGroup={createAgeGroup}
    ageGroups={ageGroups}
    ageGroupsLoading={loading}
    ageGroupsError={error}
  />;
};

